import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  FaEye, FaPlay, FaCamera, 
  FaVideo, FaEdit, FaLightbulb, FaChevronRight, FaImages,
  FaDownload, FaTrash
} from "react-icons/fa";
import { FaImage } from "react-icons/fa6";
import "./HourlyViewGallery.css";
import { getBookingByIdAPI } from "../../../../utils/APIs/bookingsApis";
import { 
  getAllGalleryImages, 
  deleteSelectedImages,
  deleteSingleImage,
  getGalleryZipLink,
  downloadGalleryZipFromCdn,
} from "../../../../utils/APIs/UploadGalleryApis";
import { getAccessToken } from "../../../../utils/APIs/commonHeadApiLogic";
import { toast } from "react-toastify";
import {
  appendLightcasePoolLinks,
  bindLightcaseLinks,
  clearLightcasePool,
  destroyLightcaseBindings,
  isVideoMediaKey,
  openLightcaseItem,
  resolveGalleryMediaUrl,
} from "../../UploadGallery/ViewGalleryPhoto/lightcaseGallery";

const LIMIT = 33;

const loadedImagesCache = new Set();

class MediaLoadQueue {
  constructor(concurrency = 4) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  register(onStart) {
    const item = { onStart };
    this.queue.push(item);
    this.next();
    return () => {
      this.queue = this.queue.filter((q) => q !== item);
    };
  }

  next() {
    if (this.running >= this.concurrency || this.queue.length === 0) return;

    this.running++;
    const item = this.queue.shift();

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.running--;
      this.next();
    };

    item.onStart(finish);
    setTimeout(finish, 2500); // 2.5s safety timeout
  }
}

const mediaLoadQueue = new MediaLoadQueue(4);

const VirtualGalleryItem = ({ children, isVideo, id, src }) => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
        }
      },
      {
        rootMargin: "1200px 0px 1200px 0px",
        threshold: 0.01,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasBeenVisible]);

  const placeholderStyle = { 
    aspectRatio: "16/10", 
    width: "100%", 
    background: "#eee", 
    borderRadius: "4px" 
  };

  const isAlreadyCached = src && loadedImagesCache.has(src);
  const placeholderClass = `gallery-placeholder-box ${isAlreadyCached ? "" : "loading-skeleton"}`;

  return (
    <div ref={containerRef} className="virtual-gallery-item-wrapper">
      {hasBeenVisible ? (
        children
      ) : (
        <div style={placeholderStyle} className={placeholderClass} />
      )}
    </div>
  );
};


const GalleryItem = React.memo(({
  src,
  isVideo,
  fileKey,
  id,
  isSelected,
  onOpenPreview,
  onDownload,
  onDelete,
  onToggleSelect,
  imageUrl
}) => {
  const [shouldLoad, setShouldLoad] = useState(() => loadedImagesCache.has(src));
  const [isLoaded, setIsLoaded] = useState(() => loadedImagesCache.has(src));
  const mediaRef = useRef(null);
  const finishLoadRef = useRef(null);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    loadedImagesCache.add(src);
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, [src]);

  const handleError = useCallback(() => {
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (shouldLoad) return;

    const unregister = mediaLoadQueue.register((finish) => {
      finishLoadRef.current = finish;
      setShouldLoad(true);
    });

    return () => {
      unregister();
      if (finishLoadRef.current) {
        finishLoadRef.current();
        finishLoadRef.current = null;
      }
    };
  }, [src, shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    const el = mediaRef.current;
    if (!el) return;

    if (isVideo) {
      if (el.readyState >= 1) {
        handleLoad();
      }
    } else {
      if (el.complete) {
        handleLoad();
      }
    }
  }, [isVideo, handleLoad, shouldLoad]);

  return (
    <div className={`gallery-box video-box ${isSelected ? 'selected' : ''} ${!isLoaded ? 'loading-skeleton' : ''}`}>
      {shouldLoad ? (
        isVideo ? (
          <video
            ref={mediaRef}
            src={src}
            className="gallery-video"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoad}
            onError={handleError}
            style={{ visibility: isLoaded ? "visible" : "hidden" }}
            onClick={() => onOpenPreview(id)}
          />
        ) : (
          <img
            ref={mediaRef}
            src={src}
            alt="gallery"
            crossOrigin="anonymous"
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            style={{ visibility: isLoaded ? "visible" : "hidden" }}
            onClick={() => onOpenPreview(id)}
          />
        )
      ) : (
        <div style={{ width: "100%", height: "100%" }} onClick={() => onOpenPreview(id)} />
      )}
      
      {isVideo && shouldLoad && isLoaded && (
        <div className="play-overlay" onClick={() => onOpenPreview(id)}>
          <div className="play-icon">
            <FaPlay className="play-triangle" />
          </div>
        </div>
      )}

      <div
        className="gallery-hover"
        onClick={(e) => {
          if (!e.target.closest("button") && !e.target.closest("input")) {
            onOpenPreview(id);
          }
        }}
      >
        <div className="gallery-actions-hover">
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(fileKey);
            }}
          >
            <FaDownload />
          </button>
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id, fileKey);
            }}
          >
            <FaTrash />
          </button>
          <button
            className="gallery-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPreview(id);
            }}
          >
            <FaEye />
          </button>
        </div>
        <div className="gallery-checkbox">
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => onToggleSelect(imageUrl)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );
});

const HourlyViewGallery = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  // -- View State --
  const [currentStep, setCurrentStep] = useState("SERVICES"); // 'SERVICES' | 'STAFF' | 'MEDIA'
  const [activeCategory, setActiveCategory] = useState("Standard");
  const [activeHours, setActiveHours] = useState("");
  const [activeService, setActiveService] = useState(null); 
  const [activePhotographer, setActivePhotographer] = useState(null);

  // -- Data State --
  const [bookingData, setBookingData] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  
  // -- Selection State --
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSidebarOpen] = useState(() => {
    const stored = sessionStorage.getItem("isSidebarOpen");
    return stored !== null ? JSON.parse(stored) : true;
  });

  const loaderRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lightcasePoolRef = useRef(null);

  const ROLES = [
    { id: 'photography', key: "photographer", label: "Photographer", icon: <FaCamera /> },
    { id: 'videography', key: "videographer", label: "Videographer", icon: <FaVideo /> },
    { id: 'editor',      key: "editor",       label: "Editor",      icon: <FaEdit /> },
    { id: 'lighting',    key: "lightingSetup",label: "Lighting Setup", icon: <FaLightbulb /> },
  ];

  const lightcaseGroup = `hourly-gallery-${bookingId}-${activePhotographer?.id || "all"}`;

  // 1. Fetch Booking Data
  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await getBookingByIdAPI(bookingId);
        if (res?.data?.success) {
          const data = res.data.data;
          setBookingData(data);
          
          // Initial tab state
          if (data.hourlyPackages?.length > 0) {
            const first = data.hourlyPackages[0];
            setActiveCategory(first.category);
            setActiveHours(first.hours);
          }
        }
      } catch (err) {
        console.error("Error fetching booking:", err);
        toast.error("Failed to load booking details");
      }
    };
    fetchBooking();
  }, [bookingId]);

  // Derived Data

  const hourTabs = useMemo(() => 
    bookingData?.hourlyPackages?.filter(pkg => pkg.category === activeCategory) || [],
    [bookingData, activeCategory]
  );

  const activePlan = useMemo(() => 
    hourTabs.find(pkg => pkg.hours === activeHours) || hourTabs[0],
    [hourTabs, activeHours]
  );

  // 2. Fetch Gallery Images
  const fetchGallery = useCallback(async (currentPage, reset = false) => {
    if (isFetchingRef.current || !activePhotographer) return;
    isFetchingRef.current = true;

    const cleanId = (val) => {
      if (!val) return "";
      if (typeof val === 'string') return val.trim().toLowerCase();
      if (typeof val === 'object') {
        if (val._id) return String(val._id).trim().toLowerCase();
        if (val.id) return String(val.id).trim().toLowerCase();
        if (typeof val.toString === 'function') return val.toString().trim().toLowerCase();
      }
      return String(val).trim().toLowerCase();
    };

    try {
      setMediaLoading(true);
      // Admin sees all photographer uploads (including unpublished). Do not filter by
      // plan category here — hourly uploads are often tagged Standard while admin
      // browses Premium, which hid every file.
      const photographerId =
        activePhotographer.id && activePhotographer.id !== "client"
          ? activePhotographer.id
          : "";
      const res = await getAllGalleryImages(
        currentPage,
        LIMIT,
        bookingId,
        "",
        photographerId ? "photographer" : "",
        photographerId
      );
      
        if (res?.data?.success) {
          const files = res.data.data || [];
          
          // Match by photographer ID/Name or "client" for raw user uploads
          const filteredFiles = files.filter(f => {
            const mediaPid = cleanId(f.photographerId);
            const targetPid = cleanId(activePhotographer.id);
            const targetName = String(activePhotographer.name || "").toLowerCase().trim();

            if (targetPid === 'client') {
              return !mediaPid; // Client uploads have no photographerId
            }

            // Match by ID
            if (mediaPid && mediaPid === targetPid) {
              return true;
            }

            // Match by Name (fallback/mismatch)
            const mediaPname = String(f.photographerId?.basicInfo?.fullName || f.photographerId?.name || f.photographerId?.username || "").toLowerCase().trim();
            if (mediaPname && targetName && mediaPname.includes(targetName)) {
              return true;
            }

            // Also check if targetPid itself is a name matching mediaPname
            if (mediaPname && String(activePhotographer.id).toLowerCase().trim() === mediaPname) {
              return true;
            }

            return false;
          });

          const normalizedFiles = filteredFiles.map((item, index) => ({
            ...item,
            id: item._id || item.id || `${currentPage}-${index}`,
            url: item.imageUrl,
          }));

          setGalleryFiles(prev => reset ? normalizedFiles : [...prev, ...normalizedFiles]);
          setHasMore(res.data.pagination?.hasMore || false);
        }
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setMediaLoading(false);
      isFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, activePhotographer]);

  useEffect(() => {
    if (currentStep === "MEDIA" && activePhotographer) {
      setPage(1);
      fetchGallery(1, true);
    }
  }, [currentStep, activePhotographer, fetchGallery]);

  // 3. Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !mediaLoading && !isFetchingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, mediaLoading]);

  useEffect(() => {
    if (page > 1) fetchGallery(page);
  }, [page, fetchGallery]);

  useEffect(() => {
    const poolNode = lightcasePoolRef.current;
    return () => {
      destroyLightcaseBindings();
      clearLightcasePool(poolNode);
    };
  }, []);

  useEffect(() => {
    const container = lightcasePoolRef.current;
    if (!container) return;

    clearLightcasePool(container);

    if (galleryFiles.length === 0) return;

    let cancelled = false;
    appendLightcasePoolLinks(container, galleryFiles, lightcaseGroup, 0, bookingId).then(() => {
      if (!cancelled) {
        bindLightcaseLinks(container);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [galleryFiles, lightcaseGroup, bookingId]);

  // -- Handlers --
  const handleBack = () => {
    if (currentStep === "MEDIA") {
      setCurrentStep("STAFF");
      setActivePhotographer(null);
      setGalleryFiles([]);
      setMediaLoading(true);
    } else if (currentStep === "STAFF") {
      setCurrentStep("SERVICES");
      setActiveService(null);
    } else {
      navigate(-1);
    }
  };

  const handleServiceClick = (service) => {
    setActiveService(service);
    setCurrentStep("STAFF");
  };

  const handlePhotographerClick = (staff) => {
    setMediaLoading(true);
    setActivePhotographer({ id: staff.id, name: staff.name });
    setCurrentStep("MEDIA");
  };

  const toggleSelect = useCallback((url) => {
    setSelectedItems((prev) =>
      prev.includes(url) ? prev.filter((item) => item !== url) : [...prev, url]
    );
  }, []);

  const buildZipFilterParams = () => ({
    bookingId,
    photographerId: activePhotographer?.id || "",
    source: activePhotographer?.id ? "photographer" : "",
    category: "",
  });


  const handleDownloadZip = async () => {
    if (!activePhotographer) {
      toast.warn("Select a staff member to download their gallery.");
      return;
    }

    if (galleryFiles.length === 0) {
      toast.warn("No files to download.");
      return;
    }

    try {
      const zipFilters = buildZipFilterParams();
      await downloadGalleryZipFromCdn(zipFilters, {
        onWaiting: () => toast.info("Preparing download from cloud storage..."),
      });
    } catch (error) {
      console.error("Download Zip Error:", error);
      toast.error(error.message || "Download Zip failed");
    }
  };

  const downloadSelected = async () => {
    const keys = galleryFiles
      .filter((f) => selectedItems.includes(f.imageUrl))
      .map((f) => f.key);

    if (keys.length === 0) {
      toast.warn("No items selected.");
      return;
    }

    try {
      const zipFilters = buildZipFilterParams();
      await downloadGalleryZipFromCdn(
        { ...zipFilters, keys },
        {
          onWaiting: () => toast.info("Preparing selected files from cloud storage..."),
        }
      );
    } catch (error) {
      console.error("Download Selected Error:", error);
      toast.error(error.message || "Download failed");
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm("Are you sure you want to delete selected items?")) return;
    try {
      const keys = galleryFiles
        .filter(f => selectedItems.includes(f.imageUrl))
        .map(f => f.key);
      await deleteSelectedImages({ bookingId, key: keys });
      setGalleryFiles(prev => prev.filter(f => !keys.includes(f.key)));
      setSelectedItems([]);
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === galleryFiles.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(galleryFiles.map(f => f.imageUrl));
    }
  };

  useEffect(() => {
    if (!bookingId || !activePhotographer || galleryFiles.length === 0) return;
    const zipFilters = buildZipFilterParams();
    getGalleryZipLink(zipFilters).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, activePhotographer, galleryFiles.length]);

  const isVideoKey = isVideoMediaKey;

  const downloadFile = useCallback(async (key) => {
    try {
      const token = getAccessToken();
      const baseUrl = (process.env.REACT_APP_PHOTOGRAPHY_ADMIN_BASE_API_URL || "").trim();

      const params = new URLSearchParams({
        key: key,
        bookingId: bookingId,
        token: token
      });

      window.location.href = `${baseUrl}/api/admins/downloadSingleFile?${params.toString()}`;
    } catch (error) {
      console.error("Single Download Error:", error);
    }
  }, [bookingId]);

  const deleteFile = useCallback(async (id, key) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    
    try {
      setMediaLoading(true);
      await deleteSingleImage({ bookingid: bookingId, key });
      setGalleryFiles(prev => prev.filter(f => f.key !== key));
      setSelectedItems(prev => prev.filter(item => item !== key));
      toast.success("File deleted successfully");
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete file");
    } finally {
      setMediaLoading(false);
    }
  }, [bookingId]);

  const openPreview = useCallback((itemId) => {
    setGalleryFiles((prev) => {
      const item = prev.find((entry) => entry.id === itemId);
      if (item) {
        if (!openLightcaseItem(itemId, undefined, bookingId, item) && item.imageUrl) {
          window.open(item.imageUrl, "_blank");
        }
      }
      return prev;
    });
  }, [bookingId]);

  // -- Render Helpers --
  const renderBreadcrumbs = () => (
    <div className="gallery-breadcrumbs mb-4">
      <span className="breadcrumb-item" onClick={() => { setCurrentStep("SERVICES"); setActiveService(null); setActivePhotographer(null); }}>
        Selection Hub
      </span>
      {activePlan && (
        <>
          <FaChevronRight className="breadcrumb-sep" />
          <span className="breadcrumb-item active" onClick={() => { setCurrentStep("SERVICES"); setActiveService(null); setActivePhotographer(null); }}>
            {activePlan.category} ({activePlan.hours})
          </span>
        </>
      )}
      {activeService && (
        <>
          <FaChevronRight className="breadcrumb-sep" />
          <span className={`breadcrumb-item ${currentStep === 'STAFF' ? 'active' : ''}`} onClick={() => { setCurrentStep("STAFF"); setActivePhotographer(null); }}>
            {activeService.label}
          </span>
        </>
      )}
      {activePhotographer && (
        <>
          <FaChevronRight className="breadcrumb-sep" />
          <span className="breadcrumb-item active">{activePhotographer.name}</span>
        </>
      )}
    </div>
  );

  const renderServiceSelection = () => {
    if (!activePlan) return <div className="text-center py-5">No packages found for this selection.</div>;

    return (
      <div className="selection-hub">
        <div className="row g-4">
          {ROLES.map(role => {
            // Robust check for booked services
            const isBookedInPlan = activePlan.services?.some(s => {
              const name = (s.name || "").toLowerCase();
              const key = (role.key || "").toLowerCase();
              const id = (role.id || "").toLowerCase();
              return name.includes(key) || name.includes(id) || (id === 'editor' && name.includes('edit'));
            });

            // Check if staff are already assigned
            let hasAssignedStaff = false;
            if (role.id === 'photography') {
                hasAssignedStaff = (activePlan.assignedPhotographerNames?.length > 0 || activePlan.assignedPhotographers?.length > 0);
            } else if (role.id === 'videography') {
                hasAssignedStaff = (activePlan.assignedVideographerNames?.length > 0 || activePlan.assignedVideographers?.length > 0);
            } else if (role.id === 'editor') {
                hasAssignedStaff = (activePlan.assignedEditorNames?.length > 0 || activePlan.assignedEditors?.length > 0);
            } else if (role.id === 'lighting') {
                hasAssignedStaff = (activePlan.assignedLightingNames?.length > 0 || activePlan.assignedLightingSetups?.length > 0);
            }

            const isEnabled = isBookedInPlan || hasAssignedStaff || role.id === 'photography';

            return (
              <div key={role.id} className="col-lg-3 col-md-6">
                <div 
                  className={`service-selection-card ${!isEnabled ? 'disabled' : ''}`}
                  onClick={() => isEnabled && handleServiceClick(role)}
                >
                  <div className="service-card-icon">{role.icon}</div>
                  <div className="service-card-label">{role.label}</div>
                  {!isEnabled && <div className="not-booked-tag">Not Booked</div>}
                  
                  <div className="service-card-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M10 16L14 12L10 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStaffFolders = () => {
    const cleanId = (val) => {
      if (!val) return "";
      if (typeof val === 'string') return val.trim().toLowerCase();
      if (typeof val === 'object') {
        if (val._id) return String(val._id).trim().toLowerCase();
        if (val.id) return String(val.id).trim().toLowerCase();
        if (typeof val.toString === 'function') return val.toString().trim().toLowerCase();
      }
      return String(val).trim().toLowerCase();
    };

    const pkg = activePlan;
    if (!pkg) {
      return (
        <div className="staff-folders-view">
          <div className="col-12 text-center py-5 bg-light rounded border">
            <p className="text-muted mb-0">No plan selected.</p>
          </div>
        </div>
      );
    }

    let ids = [];
    let names = [];

    if (activeService.id === 'photography') {
      ids = pkg.assignedPhotographers || [];
      names = pkg.assignedPhotographerNames || [];
    } else if (activeService.id === 'videography') {
      ids = pkg.assignedVideographers || [];
      names = pkg.assignedVideographerNames || [];
    } else if (activeService.id === 'editor') {
      ids = pkg.assignedEditors || [];
      names = pkg.assignedEditorNames || [];
    } else if (activeService.id === 'lighting') {
      ids = pkg.assignedLightingSetups || pkg.assignedLighting || [];
      names = pkg.assignedLightingNames || [];
    }

    const staffList = [];
    ids.forEach((idVal, index) => {
      const id = cleanId(idVal);
      if (!id) return;

      let name = names[index];
      if (!name && typeof idVal === 'object' && idVal !== null) {
        name = idVal.name || idVal.username || idVal.basicInfo?.fullName;
      }
      if (!name) name = "Unknown";

      if (!staffList.some((s) => cleanId(s.id) === id)) {
        staffList.push({ id, name });
      }
    });

    const photographers = [...staffList];

    return (
      <div className="staff-folders-view">
        <div className="row g-3">
          {photographers.length === 0 ? (
            <div className="col-12 text-center py-5 bg-light rounded border">
              <p className="text-muted mb-0">No staff assigned to this role yet.</p>
            </div>
          ) : (
            photographers.map(staff => {
              const initials = staff.name
                .split(' ')
                .slice(0, 2)
                .map(w => w[0]?.toUpperCase() || '')
                .join('');
              return (
                <div key={staff.id} className="col-md-4 col-lg-3">
                  <div className="pro-person-card" onClick={() => handlePhotographerClick(staff)}>
                    <div className="pro-person-avatar">{initials}</div>
                    <div className="pro-person-info">
                      <div className="pro-person-name">{staff.name}</div>
                    </div>
                    <div className="pro-person-action">
                      <FaImages className="pro-person-gallery-icon" />
                      <span>View Gallery</span>
                      <FaChevronRight className="pro-person-arrow" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderMedia = () => (
    <div className="media-grid-view">
      <div className="gallery-header mb-4">
        <div className="gallery-left d-flex align-items-center gap-3">
          {galleryFiles.length > 0 && (
            <label className="select-all-label d-flex align-items-center gap-2 cursor-pointer mb-0" style={{ cursor: "pointer", fontWeight: "600", fontSize: "14px", marginRight: "10px", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={
                  selectedItems.length > 0 &&
                  selectedItems.length === galleryFiles.length
                }
                onChange={handleSelectAll}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#2b1d1a" }}
              />
              Select All
            </label>
          )}
          {selectedItems.length > 0 && (
            <div className="d-flex gap-2">
              <button className="download-selected-btn" onClick={downloadSelected}>
                Download Selected ({selectedItems.length})
              </button>
              <button className="delete-selected-btn" onClick={handleDeleteSelected}>
                Delete Selected ({selectedItems.length})
              </button>
            </div>
          )}
        </div>
        <div className="gallery-actions">
          {galleryFiles.length > 0 && (
            <button className="gallery-btn" onClick={handleDownloadZip}>Download Zip</button>
          )}
        </div>
      </div>

      <div className="gallery-grid-layout">
        {galleryFiles.length > 0 ? (
          galleryFiles.map((media, idx) => (
            <VirtualGalleryItem key={media.key} isVideo={isVideoKey(media.key)} id={media.id} src={resolveGalleryMediaUrl(bookingId, media)}>
              <GalleryItem
                src={resolveGalleryMediaUrl(bookingId, media)}
                isVideo={isVideoKey(media.key)}
                fileKey={media.key}
                id={media.id}
                isSelected={selectedItems.includes(media.imageUrl)}
                onOpenPreview={openPreview}
                onDownload={downloadFile}
                onDelete={deleteFile}
                onToggleSelect={toggleSelect}
                imageUrl={media.imageUrl}
              />
            </VirtualGalleryItem>
          ))
        ) : mediaLoading ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <div className="gallery-box loading-skeleton" key={`initial-skeleton-${idx}`} />
          ))
        ) : (
          <div className="w-100 text-center py-5" style={{ gridColumn: "1 / -1" }}>
            <FaImage className="gallery-no-images-icon" />
            <h3>No Media Found</h3>
            <p>No media has been uploaded for <strong>{activePhotographer.name}</strong> yet.</p>
          </div>
        )}
      </div>

      {hasMore && (
        <div ref={loaderRef} className="text-center py-4">
          {mediaLoading && (
            <div className="spinner-border" role="status" style={{ width: "2.5rem", height: "2.5rem", color: "var(--primary-color)" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // if (loading) return <Loader />;

  return (
    <div className={`content-container ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`} style={{ marginTop: "100px" }}>
      <div className="page-inner-wrapper">
        <div className="container gallery-container">
          <div
            ref={lightcasePoolRef}
            className="lightcase-gallery-pool"
            aria-hidden="true"
          />
          
          {/* Unified Global Top-Left Back Button */}
          <div className="gallery-top-nav mb-4" style={{ display: "flex", justifyContent: "flex-start" }}>
            <button onClick={handleBack} className="admin-back-btn">
              {currentStep === "SERVICES" ? "← Back" : currentStep === "STAFF" ? "← Back" : "← Back"}
            </button>
          </div>

          {/* Fresh Page View Logic: Hide summary/tabs when navigating deeper */}
          {currentStep === "SERVICES" ? (
            <>
              <div className="mb-4">
                 <h2 className="mb-0">Hourly Gallery</h2>
              </div>

              <div className="booking-summary-banner mb-4">
                <div className="row">
                  <div className="col-12">
                    <p className="mb-1"><strong>Client:</strong> {bookingData?.client_id?.username || bookingData?.client_id?.name || "—"}</p>
                    <p className="mb-0"><strong>Booking ID:</strong> {bookingData?.veroaBookingId || bookingData?.bookingId}</p>
                  </div>
                </div>
              </div>

              {/* Hour Tabs */}
              <div className="hour-selection-tabs mb-4">
                {hourTabs.map((pkg) => (
                  <button 
                    key={pkg.hours}
                    className={`hour-tab-btn ${activeHours === pkg.hours ? 'active' : ''}`}
                    onClick={() => setActiveHours(pkg.hours)}
                  >
                    {pkg.hours}
                  </button>
                ))}
              </div>

              {renderServiceSelection()}
            </>
          ) : (
            <div className="fresh-page-view animate__animated animate__fadeIn">
               <div className="mb-4">
                 <h2 className="mb-0">
                   {currentStep === "STAFF" 
                     ? (activeService?.id === 'photography' ? 'Photographers' 
                        : activeService?.id === 'videography' ? 'Videographers' 
                        : activeService?.id === 'editor' ? 'Editors' 
                        : activeService?.id === 'lighting' ? 'Lighting Setups' 
                        : 'Folders')
                     : "Gallery"}
                 </h2>
               </div>
               
               {renderBreadcrumbs()}
               
               {currentStep === "STAFF" && renderStaffFolders()}
               {currentStep === "MEDIA" && renderMedia()}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default HourlyViewGallery;
